import React from 'react';

const PrivacyPage: React.FC = () => {
  return (
    <div className="pt-20 bg-primary-black min-h-screen">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <h1 className="text-4xl font-bold text-primary-gold mb-8 font-display">Privacy Policy</h1>
        <p className="text-gray-400 mb-8">Last Updated: March 2026</p>

        <div className="space-y-8 text-gray-300">
          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">1. Introduction</h2>
            <p>
              Welcome to The Creamery ("we," "our," "us"). We are committed to protecting and respecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit 
              our website and make reservations online.
            </p>
            <p className="mt-4">By using our website, you agree to the terms of this Privacy Policy.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">2. Information We Collect</h2>
            <p className="mb-4">We may collect and process the following types of personal data:</p>
            
            <h3 className="text-xl font-semibold text-primary-gold mb-3">a. Information You Provide to Us</h3>
            <p className="mb-2">When you make a reservation or contact us, we may collect:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Full name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Reservation details (date, time, number of guests)</li>
              <li>Special requests or preferences (e.g., dietary requirements)</li>
            </ul>

            <h3 className="text-xl font-semibold text-primary-gold mb-3 mt-4">b. Automatically Collected Information</h3>
            <p className="mb-2">When you use our website, we may automatically collect:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device information</li>
              <li>Pages visited and time spent on the site</li>
              <li>Cookies and usage data</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">3. How We Use Your Information</h2>
            <p className="mb-2">We use your information to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Manage and confirm reservations</li>
              <li>Contact you regarding your booking</li>
              <li>Provide customer service and respond to enquiries</li>
              <li>Improve our website and services</li>
              <li>Send updates or promotional offers (only if you opt in)</li>
              <li>Comply with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">4. Legal Basis for Processing (UK & GDPR)</h2>
            <p className="mb-2">We process your personal data based on:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li><strong>Contractual necessity</strong> – to manage your booking</li>
              <li><strong>Legitimate interests</strong> – to improve our services</li>
              <li><strong>Consent</strong> – for marketing communications (where applicable)</li>
              <li><strong>Legal obligations</strong> – where required by law</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">5. Sharing Your Information</h2>
            <p className="mb-4">We do not sell your personal data. However, we may share your information with:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Booking or reservation system providers</li>
              <li>IT and website service providers</li>
              <li>Payment processors (if applicable)</li>
              <li>Legal or regulatory authorities when required</li>
            </ul>
            <p className="mt-4">All third parties are required to respect the security of your data.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">6. Data Retention</h2>
            <p className="mb-2">We retain your personal data only as long as necessary to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Fulfil your reservation</li>
              <li>Meet legal, accounting, or reporting requirements</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">7. Cookies</h2>
            <p className="mb-2">Our website uses cookies to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Improve user experience</li>
              <li>Analyse website traffic</li>
              <li>Remember your preferences</li>
            </ul>
            <p className="mt-4">You can control cookies through your browser settings.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">8. Data Security</h2>
            <p>
              We implement appropriate technical and organisational measures to protect your personal data against 
              unauthorised access, loss, or misuse.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">9. Your Rights</h2>
            <p className="mb-2">Under UK data protection law, you have the right to:</p>
            <ul className="list-disc list-inside space-y-2 ml-4">
              <li>Access your personal data</li>
              <li>Request correction of inaccurate data</li>
              <li>Request deletion of your data</li>
              <li>Object to processing</li>
              <li>Withdraw consent (for marketing)</li>
              <li>Lodge a complaint with the Information Commissioner's Office (ICO)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">10. Third-Party Links</h2>
            <p>Our website may contain links to third-party websites. We are not responsible for their privacy practices.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">11. Children's Privacy</h2>
            <p>Our website is not intended for individuals under 16. We do not knowingly collect data from children.</p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">12. Changes to This Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated 
              revision date.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-white mb-4 font-display">13. Contact Us</h2>
            <p className="mb-4">If you have any questions about this Privacy Policy or your data, please contact us:</p>
            <div className="bg-gray-900 border border-primary-gold rounded-lg p-6">
              <p className="font-semibold text-white">The Creamery</p>
              <p>Bainsford Main Street</p>
              <p>Falkirk, FK2 7NU</p>
              <p className="mt-4">Email: enquiries@thecreamerysportsbar.co.uk</p>
              <p>Telephone: 01324 636330</p>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPage;
